import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  CogIcon, 
  PlayIcon,
  PauseIcon,
  TrashIcon,
  ChartBarIcon,
  ServerIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import Navbar from './Navbar';

const AIAgentConfig = () => {
  const navigate = useNavigate();
  const [agents] = useState([
    {
      id: 1,
      name: 'Customer Support Agent',
      description: 'Handles customer inquiries and support tickets',
      status: 'active',
      type: 'Support',
      model: 'GPT-4',
      lastActive: '2 minutes ago',
      requests: 1247,
      successRate: 94.2
    },
    {
      id: 2,
      name: 'Sales Assistant',
      description: 'Qualifies leads and schedules meetings',
      status: 'active',
      type: 'Sales',
      model: 'Claude-3',
      lastActive: '5 minutes ago',
      requests: 892,
      successRate: 97.8
    },
    {
      id: 3,
      name: 'Content Generator',
      description: 'Creates marketing content and blog posts',
      status: 'paused',
      type: 'Content',
      model: 'GPT-4',
      lastActive: '1 hour ago',
      requests: 456,
      successRate: 89.5
    },
    {
      id: 4,
      name: 'Data Analyst',
      description: 'Analyzes data and generates reports',
      status: 'inactive',
      type: 'Analytics',
      model: 'Claude-3',
      lastActive: '2 days ago',
      requests: 234,
      successRate: 92.1
    }
  ]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'paused':
        return 'bg-yellow-500';
      case 'inactive':
        return 'bg-gray-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'paused':
        return 'Paused';
      case 'inactive':
        return 'Inactive';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="min-h-screen bg-dark">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">AI Agent Configuration</h1>
            <p className="text-gray-300 mt-2">Manage and configure your AI agents</p>
          </div>
          <button 
            onClick={() => navigate('/ai-agents/new')}
            className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Create New Agent</span>
          </button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Agents</p>
                <p className="text-2xl font-bold text-white">{agents.length}</p>
              </div>
              <ServerIcon className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Active Agents</p>
                <p className="text-2xl font-bold text-white">{agents.filter(a => a.status === 'active').length}</p>
              </div>
              <BoltIcon className="h-8 w-8 text-green-500" />
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Requests</p>
                <p className="text-2xl font-bold text-white">{agents.reduce((sum, agent) => sum + agent.requests, 0).toLocaleString()}</p>
              </div>
              <ChartBarIcon className="h-8 w-8 text-purple-500" />
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Avg Success Rate</p>
                <p className="text-2xl font-bold text-white">
                  {(agents.reduce((sum, agent) => sum + agent.successRate, 0) / agents.length).toFixed(1)}%
                </p>
              </div>
              <ChartBarIcon className="h-8 w-8 text-yellow-500" />
            </div>
          </div>
        </div>

        {/* Agents Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {agents.map((agent) => (
            <div key={agent.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-gray-600 transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(agent.status)} animate-pulse`}></div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{agent.name}</h3>
                    <p className="text-sm text-gray-400">{agent.type}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => navigate(`/ai-agents/${agent.id}`)}
                    className="text-gray-400 hover:text-white"
                  >
                    <CogIcon className="h-5 w-5" />
                  </button>
                  {agent.status === 'active' ? (
                    <button className="text-gray-400 hover:text-yellow-400">
                      <PauseIcon className="h-5 w-5" />
                    </button>
                  ) : (
                    <button className="text-gray-400 hover:text-green-400">
                      <PlayIcon className="h-5 w-5" />
                    </button>
                  )}
                  <button className="text-gray-400 hover:text-red-400">
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <p className="text-gray-300 text-sm mb-4">{agent.description}</p>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Status:</span>
                  <span className={`text-sm font-medium ${
                    agent.status === 'active' ? 'text-green-400' :
                    agent.status === 'paused' ? 'text-yellow-400' : 'text-gray-400'
                  }`}>
                    {getStatusText(agent.status)}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Model:</span>
                  <span className="text-white text-sm">{agent.model}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Requests:</span>
                  <span className="text-white text-sm">{agent.requests.toLocaleString()}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Success Rate:</span>
                  <span className="text-white text-sm">{agent.successRate}%</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Last Active:</span>
                  <span className="text-white text-sm">{agent.lastActive}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-700">
                <div className="flex space-x-2">
                  <button 
                    onClick={() => navigate(`/ai-agents/${agent.id}`)}
                    className="flex-1 bg-primary text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
                  >
                    Configure
                  </button>
                  <button className="flex-1 bg-gray-700 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium">
                    View Logs
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Create New Agent Card */}
          <div 
            onClick={() => navigate('/ai-agents/new')}
            className="bg-gray-800 rounded-lg p-6 border-2 border-dashed border-gray-600 hover:border-primary transition-colors cursor-pointer flex flex-col items-center justify-center text-center min-h-[300px]"
          >
            <PlusIcon className="h-12 w-12 text-gray-500 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Create New Agent</h3>
            <p className="text-gray-400 text-sm">Set up a new AI agent to automate your workflows</p>
          </div>
        </div>

        {/* Agent Templates */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-white mb-6">Popular Agent Templates</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: 'Customer Support', icon: '🎧', description: 'Handle customer inquiries' },
              { name: 'Sales Assistant', icon: '💼', description: 'Qualify leads and schedule meetings' },
              { name: 'Content Writer', icon: '✍️', description: 'Generate marketing content' },
              { name: 'Data Analyst', icon: '📊', description: 'Analyze data and create reports' }
            ].map((template, index) => (
              <div key={index} className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-gray-600 transition-colors cursor-pointer">
                <div className="text-2xl mb-2">{template.icon}</div>
                <h3 className="text-white font-semibold mb-1">{template.name}</h3>
                <p className="text-gray-400 text-sm">{template.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAgentConfig;